import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useLoopReturnsIntegrationQuery } from "modules/settings/hooks/loopReturns/useLoopReturnsIntegrationQuery"
import { useUpsertLoopReturnsIntegrationMutation } from "modules/settings/hooks/loopReturns/useUpsertLoopReturnsIntegrationMutation"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertLoopReturnsIntegrationSchema } from "schema"
import { z } from "zod"

export const LoopReturnsAuthModal = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof UpsertLoopReturnsIntegrationSchema>>({
    resolver: zodResolver(UpsertLoopReturnsIntegrationSchema),
  })
  const navigate = useNavigate()
  const { mutateAsync } = useUpsertLoopReturnsIntegrationMutation()
  const { data } = useLoopReturnsIntegrationQuery()

  const onSubmit = async (data: z.infer<typeof UpsertLoopReturnsIntegrationSchema>) => {
    await mutateAsync(data)
    onOpenChange()
  }

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      navigate({ to: "/settings/integrations/loopReturns" })
    }
  }

  useEffect(() => {
    if (data) {
      form.reset({
        apiKey: data.apiKey ?? "",
      })
    }
  }, [data, form])

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.loopReturns.authModal.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.loopReturns.authModal.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <div className="flex flex-col w-full gap-4">
              <FormField
                control={form.control}
                name="apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("settings.integrations.loopReturns.authModal.form.apiKey")}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} required />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button disabled={form.formState.isLoading || !form.formState.isValid}>
                {t("settings.integrations.loopReturns.authModal.form.submit")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
